import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import { FormField } from 'components/form';
import Tooltip from 'components/Tooltip';
import CheckboxesList from 'components/inputs/checkboxes-list/CheckboxesList';

import SOVQueriesInputField from 'modules/insights/sov/components/queries/inputs/field/SOVQueriesInputField';

import { resetCheckboxes, updateCheckboxes } from 'modules/insights/sov/reducers/queries/collabary/youtube/sov-queries-youtube';

import { __t, applyTranslations } from 'utils/language';
import { EDIT } from 'modules/insights/sov/utils/sov';
import { MIN_CHAR_COUNT_FOR_QUERY, SINGLE } from 'modules/insights/sov/utils/queries/sov-queries';

import './sov-queries-modal-collabary-youtube.scss';

import type { CheckboxesListItem } from 'types';
import type {
  SOVGeneralActionType,
  SOVGeneralModalTextFields,
  SOVQueryLocal,
  SOVQueryStepType,
  SOVQueryType,
  SOVReport
} from 'modules/insights/sov/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/components/queries/modals/sov-queries-modal.i18n.json'));

type OwnProps = {
  report: SOVReport,
  query: SOVQueryLocal,
  actionType: SOVGeneralActionType,
  stepIndex: number,
  stepCount: number,
  textFields: SOVGeneralModalTextFields,
  onRenderActionButton: (isDisabled: boolean) => React.ReactNode | undefined | null,
  onRenderCancelButton: () => React.ReactNode | undefined | null,
  onTextFieldChange: (fieldType: SOVQueryStepType, value: string) => void
};

type StateProps = {
  checkboxes: Array<CheckboxesListItem>
};

type DispatchProps = {
  dispatchUpdateCheckboxes: (a: Array<CheckboxesListItem>) => void,
  dispatchResetCheckboxes: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isActionButtonDisabled: boolean
};

const COLLABARY_MSG_KEY = 'collabary_common';
const MODAL_MSG_KEY = 'sov_queries_modal';

class SOVQueriesModalCollabaryYoutube extends React.PureComponent<Props, State> {
  state = {
    isActionButtonDisabled: true
  };

  componentDidMount() {
    const { dispatchUpdateCheckboxes } = this.props;
    const updatedCheckboxes = this.getUpdatedCheckboxes();

    this.getIsActionButtonDisabledStatus();

    dispatchUpdateCheckboxes(updatedCheckboxes);
  }

  componentDidUpdate() {
    this.getIsActionButtonDisabledStatus();
  }

  componentWillUnmount() {
    const { dispatchResetCheckboxes } = this.props;

    this.setActionButtonDisabledStatus(true);
    dispatchResetCheckboxes();
  }

  getIsActionButtonDisabledStatus(): void {
    const { checkboxes, textFields } = this.props;
    const filteredCheckboxes = checkboxes.filter(item => item.isSelected);
    const result = textFields[SINGLE].length < MIN_CHAR_COUNT_FOR_QUERY
      || isEmpty(filteredCheckboxes);

    this.setActionButtonDisabledStatus(result);
  }

  getUpdatedCheckboxes(): Array<CheckboxesListItem> {
    const { query, checkboxes, actionType } = this.props;

    return checkboxes.map(item => {
      // $FlowIgnore - Flow doesn't like that YouTube keywords are handled differently than Instagram
      const foundItem = (query.includedKeywords || []).some(k => k.type === item.type);
      const isSelected = foundItem || item.isSelected;

      return {
        ...item,
        isSelected: actionType === EDIT ? foundItem : isSelected
      };
    });
  }


  setActionButtonDisabledStatus = (status: boolean): void => {
    this.setState({
      isActionButtonDisabled: status
    });
  };

  handleCheckboxToggled = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    const { checkboxes, dispatchUpdateCheckboxes } = this.props;

    const updatedCheckboxes = checkboxes.map(checkbox => ({
      ...checkbox,
      isSelected: checkbox.id === item.id ? !item.isSelected : checkbox.isSelected
    }));

    dispatchUpdateCheckboxes(updatedCheckboxes);
  };

  renderRequiredMark(): React.ReactNode {
    return (
      <div className="sov-queries-modal-collabary-youtube__required form-required-mark">
        *
      </div>
    );
  }

  renderTooltip(queryType: SOVQueryType): React.ReactNode {
    const { report } = this.props;

    return (
      <div className="sov-queries-modal-collabary-youtube__tooltip">
        <Tooltip
          message={__t(`${MODAL_MSG_KEY}.tooltips.enter_${queryType}`, {
            channel: __t(`${COLLABARY_MSG_KEY}.social.${report.reportType}`)
          })}
          width="long"
          showQuestionMark
        />
      </div>
    );
  }

  renderFooter(): React.ReactNode {
    const { onRenderActionButton, onRenderCancelButton } = this.props;
    const { isActionButtonDisabled } = this.state;

    return (
      <ModalFrameFooter
        className="sov-queries-modal-collabary-youtube__footer"
      >
        {onRenderCancelButton()}
        {onRenderActionButton(isActionButtonDisabled)}
      </ModalFrameFooter>
    );
  }

  renderTextField(): React.ReactNode {
    const { textFields, onTextFieldChange } = this.props;

    return (
      <FormField
        direction="column"
        label=""
        showValidationIcon={false}
        data={{ type: SINGLE }}
      >
        <SOVQueriesInputField
          queryType={SINGLE}
          fieldType={SINGLE}
          value={textFields[SINGLE]}
          isDisabled={false}
          isChecked
          autoFocus
          onChange={onTextFieldChange}
        />
      </FormField>
    );
  }

  renderCheckboxes(): React.ReactNode {
    const { checkboxes } = this.props;

    return (
      <FormField
        direction="column"
        label={__t(`${MODAL_MSG_KEY}.messages.select_section`)}
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
        showRequiredMark
      >
        <div className="sov-queries-modal-collabary-youtube__checkboxes">
          <CheckboxesList
            lists={checkboxes}
            emptyLabel=""
            size="small"
            data={{ type: 'youtube' }}
            onCheckboxChange={this.handleCheckboxToggled}
          />
        </div>
      </FormField>
    );
  }

  renderBody(): React.ReactNode {
    return (
      <ModalFrameInner
        minHeight={359}
      >
        <div className="sov-queries-modal-collabary-youtube__body">
          <div className="sov-queries-modal-collabary-youtube__title">
            {__t(`${MODAL_MSG_KEY}.messages.define_keyword`)}
            {this.renderRequiredMark()}
            {this.renderTooltip(SINGLE)}
          </div>
          {this.renderTextField()}
          {this.renderCheckboxes()}
        </div>
      </ModalFrameInner>
    );
  }

  render(): React.ReactNode {
    const { actionType, stepIndex, stepCount } = this.props;

    return (
      <ModalFrame
        title={__t(`${MODAL_MSG_KEY}.labels.${actionType}`)}
        classNameFrame="sov-queries-modal-collabary-youtube"
        stepCounter={{
          index: stepIndex + 1,
          count: stepCount
        }}
      >
        {this.renderBody()}
        {this.renderFooter()}
      </ModalFrame>
    );
  }
}

const mapStateToProps = (
  {
    sovQueriesYoutube
  }
): StateProps => ({
  checkboxes: sovQueriesYoutube.checkboxes
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateCheckboxes: (checkboxes: Array<CheckboxesListItem>) => (
    dispatch(updateCheckboxes(checkboxes))
  ),

  dispatchResetCheckboxes: () => (
    dispatch(resetCheckboxes())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVQueriesModalCollabaryYoutube);
